/**
 * MarketData DSL helpers — date/time parsing, bar serialization, count limits.
 */

import { ArgumentError } from "../errors";
import type { OHLCData } from "@/vybe-trader-client/chart/types";
import { alignTimestampToTimeframe } from "@/lib/market-data-db/align-timestamp";
import type { TimeFrame } from "@/vybe-trader-client/chart/types";

const DAY_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

export const MAX_MARKET_DATA_BAR_COUNT = 100;

export interface SerializedBar {
  day: string;
  timestampMs: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export function dayKeyFromTimestampMs(ts: number): string {
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseMarketDataDate(raw: string): string {
  const s = raw.trim().slice(0, 10);
  if (!DAY_KEY_RE.test(s)) {
    throw new ArgumentError(
      `Date must be yyyy-MM-dd, got '${raw}'`,
      { raw },
    );
  }
  return s;
}

export function parseMarketDataTime(raw: string): string {
  const t = raw.trim();
  const match = TIME_RE.exec(t);
  if (!match) {
    throw new ArgumentError(
      `Time must be HH:mm or HH:mm:ss, got '${raw}'`,
      { raw },
    );
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const second = match[3] != null ? Number(match[3]) : 0;
  if (
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    second < 0 ||
    second > 59
  ) {
    throw new ArgumentError(`Time out of range, got '${raw}'`, { raw });
  }
  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");
  const ss = String(second).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

/** Parse date + time as local wall-clock; returns epoch ms. */
export function parseMarketDataAnchor(date: string, time: string): number {
  const normalizedTime = parseMarketDataTime(time);
  const anchor = new Date(`${date}T${normalizedTime}`);
  if (Number.isNaN(anchor.getTime())) {
    throw new ArgumentError(`Invalid date/time: ${date} ${time}`, {
      date,
      time,
    });
  }
  return anchor.getTime();
}

export function alignMarketDataAnchor(
  timestampMs: number,
  timeframe: TimeFrame,
): number {
  return alignTimestampToTimeframe(timestampMs, timeframe);
}

export function clampBarCount(raw: number): {
  value: number;
  clamped: boolean;
} {
  if (!Number.isInteger(raw) || raw < 0) {
    throw new ArgumentError(
      `Bar count must be a non-negative integer, got ${raw}`,
      { raw },
    );
  }
  if (raw > MAX_MARKET_DATA_BAR_COUNT) {
    return { value: MAX_MARKET_DATA_BAR_COUNT, clamped: true };
  }
  return { value: raw, clamped: false };
}

export function serializeOhlcBar(bar: OHLCData): SerializedBar {
  const timestampMs = bar.timestamp;
  return {
    day: dayKeyFromTimestampMs(timestampMs),
    timestampMs,
    open: bar.open,
    high: bar.high,
    low: bar.low,
    close: bar.close,
    volume: bar.volume,
  };
}

export function serializeOhlcBars(bars: OHLCData[]): SerializedBar[] {
  return bars.map(serializeOhlcBar);
}

export function parseMarketDataRangeStart(date: string): number {
  const d = parseMarketDataDate(date);
  return new Date(`${d}T00:00:00`).getTime();
}

export function parseMarketDataRangeEnd(date: string): number {
  const d = parseMarketDataDate(date);
  return new Date(`${d}T23:59:59.999`).getTime();
}
